import React, { useState, useEffect } from 'react';
import { useSchool } from '../../context/SchoolContext';
import { useNavigate } from 'react-router-dom';
import { HiMenuAlt2, HiX, HiOutlineCollection } from 'react-icons/hi';
import Sidebar from '../../components/layout/Sidebar';
import Summary from './Summary';
import AddNew from './AddNew';
import ViewAll from './ViewAll';
import BrandLoader from '../../components/feedback/BrandLoader';

const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'addnew', label: 'Add New' },
  { key: 'viewall', label: 'View All' },
];

function Dashboard() {
  const {
    sections,
    terms,
    selectedTerm,
    currentTerm,
    selectTerm,
    loading,
    error,
  } = useSchool();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (sections && sections.length > 0 && !activeSection) {
      setActiveSection(sections[0]);
    }
  }, [sections, activeSection]);

  if (loading) {
    return <BrandLoader />;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-neutral-950 dark:text-neutral-100 lg:fixed lg:inset-0 lg:flex lg:h-screen lg:w-screen lg:overflow-hidden">

      {/* Sidebar */}
      <div className="hidden lg:block lg:h-screen lg:shrink-0">
        <Sidebar
          activeSection={activeSection}
          setActiveSection={(section) => {
            setActiveSection(section);
            setActiveTab('summary');
          }}
        />
      </div>

      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Close sections menu"
            className="absolute inset-0 bg-neutral-800/60"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative z-10 h-full w-72 max-w-[85vw] bg-white shadow-xl dark:bg-neutral-900">
            <Sidebar
              activeSection={activeSection}
              setActiveSection={(section) => {
                setActiveSection(section);
                setActiveTab('summary');
              }}
              onSectionSelect={() => setSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 lg:h-screen lg:overflow-hidden">

        {/* Top Bar */}
        <div className="shrink-0 border-b border-gray-100 bg-white px-4 py-4 shadow-sm dark:border-neutral-800 dark:bg-neutral-900 sm:px-6 lg:px-8 lg:py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                aria-label={sidebarOpen ? 'Close sections menu' : 'Open sections menu'}
                onClick={() => setSidebarOpen(current => !current)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-300 dark:hover:bg-neutral-800 lg:hidden"
              >
                {sidebarOpen ? <HiX className="text-xl" /> : <HiMenuAlt2 className="text-xl" />}
              </button>
              <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-neutral-800 dark:text-neutral-100 sm:flex">
                <HiOutlineCollection className="text-2xl" />
              </div>
              <div className="min-w-0">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Section
                  </span>
                  {activeSection?.initials && (
                    <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500 dark:bg-neutral-800 dark:text-neutral-300">
                      {activeSection.initials}
                    </span>
                  )}
                </div>
                <h1 className="truncate text-xl font-bold text-gray-900 dark:text-neutral-100 sm:text-2xl">
                  {activeSection?.name || 'Select a Section'}
                </h1>
              </div>
            </div>

            {/* Tabs */}
            <div className="hidden items-center gap-3 lg:flex">
              {terms?.length > 0 && (
                <label className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Term
                  </span>
                  <select
                    value={selectedTerm?.id || ''}
                    onChange={(event) => selectTerm(event.target.value)}
                    className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 outline-none transition focus:ring-2 focus:ring-blue-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-200"
                  >
                    {terms.map(term => (
                      <option key={term.id} value={term.id}>
                        {formatTermLabel(term)}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-neutral-800">
                {TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      activeTab === tab.key
                        ? 'bg-white text-gray-900 shadow-sm dark:bg-neutral-950 dark:text-neutral-100'
                        : 'text-gray-400 hover:text-gray-600 dark:hover:text-neutral-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {terms?.length > 0 && (
            <div className="mt-4 lg:hidden">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Term
                </span>
                <select
                  value={selectedTerm?.id || ''}
                  onChange={(event) => selectTerm(event.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 outline-none transition focus:ring-2 focus:ring-blue-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-200"
                >
                  {terms.map(term => (
                    <option key={term.id} value={term.id}>
                      {formatTermLabel(term)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {selectedTerm && selectedTerm.id !== currentTerm?.id && (
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300">
              Viewing previous term records. Add New is available only for the current active term.
            </div>
          )}

          {/* Tabs */}
          <div className="mt-4 grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1 dark:bg-neutral-800 lg:hidden">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg px-2 py-2 text-xs font-semibold transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-neutral-950 dark:text-neutral-100'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-neutral-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-0 flex-1 p-4 sm:p-6 lg:overflow-y-auto lg:overscroll-contain lg:p-8">
          {error && (
            <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-700 dark:border-blue-800 dark:bg-neutral-900 dark:text-blue-300">
              {error}
            </div>
          )}

          {activeSection ? (
            <>
              {activeTab === 'summary' && <Summary section={activeSection} selectedTerm={selectedTerm} />}
              {activeTab === 'addnew' && (
                <AddNew
                  section={activeSection}
                  selectedTerm={selectedTerm}
                  currentTerm={currentTerm}
                />
              )}
              {activeTab === 'viewall' && <ViewAll section={activeSection} selectedTerm={selectedTerm} />}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="max-w-md text-center">
                <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-2">No sections found</h2>
                <p className="text-gray-500 dark:text-neutral-400 mb-5">
                  Add at least one section so the dashboard can show forms, records, and summaries.
                </p>
                <button
                  onClick={() => navigate('/onboarding/sections')}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-xl transition-all"
                >
                  Add Sections
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default Dashboard;

function formatTermLabel(term) {
  const status = term.status === 'active' ? 'Current' : 'Previous';
  return `${term.name} ${term.academic_year} (${status})`;
}
